import React from 'react';
import { Grid, SvgIcon, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

import LightTooltip from 'components/common/LightTooltip';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'i18n';

interface TooltipInfoProps {
  titleKey?: string;
  textKey?: string;
  textNode?: React.ReactNode;
  hasTitle?: boolean;
  icon?: React.FC<React.PropsWithChildren<React.SVGProps<SVGSVGElement>>>;
  placement?:
    | 'bottom'
    | 'left'
    | 'right'
    | 'top'
    | 'left-start'
    | 'bottom-end'
    | 'bottom-start'
    | 'left-end'
    | 'right-end'
    | 'right-start'
    | 'top-end'
    | 'top-start';
  width?: number;
  padding?: string;
  varinat?:
    | 'inherit'
    | 'button'
    | 'caption'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'subtitle2'
    | 'subtitle1'
    | 'body1'
    | 'body2'
    | 'overline';
}

interface StyleParams {
  width?: number;
  padding?: string;
}

const useStyle = makeStyles<StyleParams>()((theme, params) => ({
  tooltip: {
    width: params?.width || 315,
    padding: params?.padding || '16px 16px 24px 16px',
    [theme.breakpoints.down('sm')]: {
      width: 235,
      padding: '12px 12px 16px 12px',
    },
  },
  tooltipTitle: {
    textTransform: 'uppercase',
  },
  tooltipText: {
    fontWeight: 500,
    // color: '#7B8BAD',
    whiteSpace: 'pre-line',
  },
  infoIconWrapper: {
    width: 16,
    height: 16,
  },
  infoSWGIcon: {
    width: 16,
    height: 16,
    fill: 'none',
  },
  infoIcon: {
    color: '#c4c4c4',
    fontSize: 17,
  },
  textNode: {
    whiteSpace: 'pre-line',
  },
}));

const TooltipInfo: React.FC<React.PropsWithChildren<TooltipInfoProps>> = ({
  titleKey = 'screen.eventDashboard.toolTips.helpfulTips',
  textKey,
  textNode,
  icon,
  placement = 'left-start',
  hasTitle = false,
  width,
  padding,
  varinat = 'body2',
}) => {
  const [t] = useTranslation('admin');
  const { classes } = useStyle({ width, padding });

  return (
    <LightTooltip
      title={
        <Grid className={classes.tooltip} container spacing={2}>
          {hasTitle && (
            <Grid item xs={12}>
              <Typography className={classes.tooltipTitle} variant="subtitle2" color="textPrimary" component="p">
                {t(titleKey)}
              </Typography>
            </Grid>
          )}
          {textKey && (
            <Grid item xs={12}>
              <Typography className={classes.tooltipText} variant={varinat} color="textPrimary" component="p">
                {t(textKey)}
              </Typography>
            </Grid>
          )}
          {textNode && (
            <Grid item xs={12}>
              <Typography className={classes.textNode} variant={varinat} color="textPrimary" component="p">
                {textNode}
              </Typography>
            </Grid>
          )}
        </Grid>
      }
      enterTouchDelay={0}
      placement={placement}
      borderRadius={8}
      arrow
      border
      leaveTouchDelay={2000}
      PopperProps={{
        disablePortal: true,
      }}
    >
      <div className={classes.infoIconWrapper}>
        {icon ? (
          <SvgIcon className={classes.infoSWGIcon} component={icon} viewBox="0 0 16 16" />
        ) : (
          <InfoIcon className={classes.infoIcon} />
        )}
      </div>
    </LightTooltip>
  );
};

export default TooltipInfo;
